// Core browser and device detection utilities

// Version constants for device compatibility
const MIN_MODERN_IOS_VERSION = 14; // iOS 14+ is considered modern
const MIN_MODERN_ANDROID_VERSION = 8; // Android 8+ is considered modern

export const isMobile = (): boolean =>
  /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

export const isIOS = (): boolean => /iPad|iPhone|iPod/.test(navigator.userAgent);

export const isSafari = (): boolean => {
  const { userAgent } = navigator;
  return (
    userAgent.includes('Safari') && !userAgent.includes('Chrome') && !userAgent.includes('Chromium')
  );
};

export const isChromeOnIOS = (): boolean => {
  const { userAgent } = navigator;
  return isIOS() && (userAgent.includes('CriOS') || userAgent.includes('Chrome'));
};

export const isIOSSafari = (): boolean => isIOS() && isSafari();

// Check for in-app browser user agent indicators
export function detectInAppBrowserByUserAgent(): boolean {
  const userAgent = navigator.userAgent || '';
  const userAgentLower = userAgent.toLowerCase();

  const inAppBrowserIndicators = [
    'fban',
    'fbav',
    'instagram',
    'twitter',
    'linkedinapp',
    'snapchat',
    'pinterest',
    'whatsapp',
    'messenger',
    'slack',
    'electron',
  ];

  // LINE messenger needs its own bounded check: the bare substring 'line'
  // matched every LineageOS Android UA ("Build/LineageOS…"), forcing those
  // respondents onto the in-app-browser recording path (TELV1-543). The
  // LINE app stamps a "Line/<version>" product token.
  const isLineMessenger = /\bline\/[\d.]/i.test(userAgent);

  return (
    isLineMessenger || inAppBrowserIndicators.some((keyword) => userAgentLower.includes(keyword))
  );
}

// Test if camera/getUserMedia is working properly
export async function checkCameraAvailability(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) return false;

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    const [track] = stream.getVideoTracks();
    await new Promise((resolve) => {
      setTimeout(resolve, 500);
    });
    const isTrackLive = track.readyState === 'live';
    track.stop();
    return isTrackLive;
  } catch (err) {
    return false;
  }
}

export const isFirefoxMobile = (): boolean => {
  const { userAgent } = navigator;
  return userAgent?.includes('Firefox') && /Mobi|Android/.test(userAgent);
};

export const isFirefoxAndroid = (): boolean => {
  const { userAgent } = navigator;
  return userAgent?.includes('Firefox') && /Android/.test(userAgent);
};

export const isAndroid = (): boolean => {
  const { userAgent } = navigator;
  return /Android/.test(userAgent);
};

export const getBrowserInfo = (): { name: string; version: string } => {
  const { userAgent } = navigator;

  const browsers = [
    { name: 'Firefox', pattern: /Firefox\/(\d+)/, condition: () => userAgent.includes('Firefox') },
    { name: 'Chrome', pattern: /Chrome\/(\d+)/, condition: () => userAgent.includes('Chrome') },
    {
      name: 'Safari',
      pattern: /Version\/(\d+)/,
      condition: () => userAgent.includes('Safari') && !userAgent.includes('Chrome'),
    },
    { name: 'Edge', pattern: /Edge\/(\d+)/, condition: () => userAgent.includes('Edge') },
    { name: 'Edge (Chromium)', pattern: /Edg\/(\d+)/, condition: () => userAgent.includes('Edg/') },
  ];

  const detectedBrowser = browsers.find((browser) => browser.condition());

  if (detectedBrowser) {
    const match = userAgent.match(detectedBrowser.pattern);
    return {
      name: detectedBrowser.name,
      version: match?.[1] ?? 'Unknown',
    };
  }

  return { name: 'Unknown', version: 'Unknown' };
};

export const isOldDevice = (): boolean => {
  const { userAgent } = navigator;

  // Check iOS versions
  if (isIOS()) {
    const iosMatch = userAgent.match(/OS (\d+)_/);
    if (iosMatch) {
      const version = parseInt(iosMatch[1], 10);
      return version < MIN_MODERN_IOS_VERSION;
    }
  }

  // Check Android versions
  if (userAgent.includes('Android')) {
    const androidMatch = userAgent.match(/Android (\d+)/);
    if (androidMatch) {
      const version = parseInt(androidMatch[1], 10);
      return version < MIN_MODERN_ANDROID_VERSION;
    }
  }

  return false;
};

export const isBrowserSupported = (): boolean =>
  !!(
    typeof window !== 'undefined' &&
    window.MediaRecorder &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia
  );
